<template>
  <div class="ai-creation-page">
    <div class="creation-container">
      <div class="creation-header">
        <h1>AI 创作平台</h1>
        <p>输入提示词，释放你的创意灵感</p>
      </div>

      <!-- 创作区域 -->
      <div class="creation-panel">
        <div class="creation-tabs">
          <div
            class="tab-item"
            :class="{ active: creationMode === 'text-to-image' }"
            @click="creationMode = 'text-to-image'"
          >
            <i class="fas fa-image"></i> 文生图
          </div>
          <div
            class="tab-item"
            :class="{ active: creationMode === 'image-to-3d' }"
            @click="creationMode = 'image-to-3d'"
          >
            <i class="fas fa-cube"></i> 图生3D模型
          </div>
        </div>

        <div v-if="creationMode === 'text-to-image'">
        <div class="input-section">
          <div class="input-header">
            <label class="input-label">
              <i class="fas fa-pen-fancy"></i> 提示词
            </label>
            <div class="coin-tip" v-if="creationCost > 0">
              <i class="fas fa-info-circle"></i>
              本次生成预计消耗 <span class="coin-amount">{{ creationCost }}</span> 虚拟豆
            </div>
          </div>
          <div class="textarea-wrapper">
            <textarea
              v-model="prompt"
              placeholder="描述你想生成的画面，例如：分别生成通用的脂肪族多元酸酐、芳香族多元酸酐和多元酸等酯化剂的分子结构式通用表示"
              class="prompt-input"
              rows="4"
            ></textarea>
            <div class="word-count">{{ prompt.length }}/1000</div>
          </div>
        </div>

        <div class="settings-section">
          <div class="settings-left">
            <div class="ratio-selector">
              <span class="label">图片比例：</span>
              <div class="ratio-options">
                <button
                  v-for="ratio in quickRatios"
                  :key="ratio.value"
                  :class="['ratio-btn', { active: selectedRatio === ratio.value }]"
                  @click="selectedRatio = ratio.value"
                >
                  <div class="ratio-icon" :style="getRatioStyle(ratio.value)"></div>
                  <span>{{ ratio.label }}</span>
                </button>
              </div>
              <div class="ratio-dropdown-wrapper">
                <select v-model="selectedRatio" class="ratio-dropdown">
                  <option v-for="ratio in ratios" :key="ratio.value" :value="ratio.value">
                    {{ ratio.label }}
                  </option>
                </select>
              </div>
            </div>
            
            <!-- 参考图片选项按钮 -->
            <div class="reference-image-option" :class="{ active: referenceImagePreview }" @click.stop="toggleReferenceImageMenu">
              <input
                type="file"
                ref="referenceImageInput"
                accept="image/*"
                style="display: none"
                :disabled="isGenerating"
                @change="handleReferenceImageUpload"
              >
              <button class="option-btn" :class="{ 'has-image': referenceImagePreview }">
                <div v-if="referenceImagePreview" class="reference-image-thumbnail">
                  <img :src="referenceImagePreview" alt="参考图">
                </div>
                <i v-else class="fas fa-plus"></i>
                <span v-if="!referenceImagePreview">添加参考图</span>
                <span v-else>参考图</span>
              </button>
              
              <!-- 下拉菜单 -->
              <div v-if="showReferenceImageMenu" class="reference-image-menu">
                <div v-if="!referenceImagePreview" class="menu-item" @click.stop="triggerImageUpload">
                  <i class="fas fa-paperclip"></i>
                  <span>上传图片</span>
                </div>
                <div v-else class="menu-item image-preview-item">
                  <img :src="referenceImagePreview" class="menu-preview-img">
                  <div class="image-info">
                    <span class="image-name">参考图片</span>
                    <span class="image-size">{{ formatFileSize(referenceImage.size) }}</span>
                  </div>
                  <div class="menu-actions">
                    <button class="change-image-btn" @click.stop="triggerImageUpload">
                      <i class="fas fa-sync-alt"></i> 更换
                    </button>
                    <button class="remove-menu-btn" @click.stop="removeReferenceImage">
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <button class="generate-btn" @click="handleGenerate" :disabled="isGenerating || (!prompt.trim() && !referenceImagePreview)">
            <i class="fas" :class="isGenerating ? 'fa-spinner fa-spin' : 'fa-magic'"></i>
            {{ isGenerating ? '正在生成...' : '立即生成' }}
            <span v-if="!isGenerating" class="cost-info">
              (-{{ creationCost }} <i class="fas fa-coins" style="color: #FFD700; margin-left: 2px;"></i>)
            </span>
          </button>
        </div>
        </div>

        <div v-else-if="creationMode === 'image-to-3d'" class="img-to-3d-panel">
          <div class="input-section">
            <div class="input-header">
              <label class="input-label">
                <i class="fas fa-image"></i> 上传图片
              </label>
              <div class="coin-tip" v-if="imgTo3dCost > 0">
                <i class="fas fa-info-circle"></i>
                本次生成预计消耗 <span class="coin-amount">{{ imgTo3dCost }}</span> 虚拟豆
              </div>
            </div>

            <div class="upload-area" :class="{ disabled: isImgTo3dGenerating }" @click="!isImgTo3dGenerating && $refs.fileInput.click()">
              <input
                type="file"
                ref="fileInput"
                accept="image/*"
                style="display: none"
                :disabled="isImgTo3dGenerating"
                @change="handleImageUpload"
              >
              <div v-if="!uploadedImagePreview" class="upload-placeholder">
                 <i class="fas fa-cloud-upload-alt"></i>
                 <p>点击或拖拽上传图片</p>
                 <p class="sub-text">支持 JPG, PNG, max 10MB</p>
              </div>
              <div v-else class="preview-container">
                 <img :src="uploadedImagePreview" class="preview-img">
                 <div class="reupload-overlay">
                    <i class="fas fa-sync-alt"></i> 点击重新上传
                 </div>
              </div>
            </div>
          </div>

          <div class="settings-section" style="justify-content: flex-end;">
             <button class="generate-btn" @click="handleImgTo3dGenerate" :disabled="isImgTo3dGenerating || !uploadedImagePreview">
               <i class="fas" :class="isImgTo3dGenerating ? 'fa-spinner fa-spin' : 'fa-magic'"></i>
               {{ isImgTo3dGenerating ? '正在生成...' : '立即生成' }}
               <span v-if="!isImgTo3dGenerating && imgTo3dCost > 0" class="cost-info">
                 (-{{ imgTo3dCost }} <i class="fas fa-coins" style="color: #FFD700; margin-left: 2px;"></i>)
               </span>
             </button>
          </div>

          <div v-if="imgTo3dResult" class="result-section">
             <h3>生成结果</h3>
             <div class="result-card">
                <!-- 3D 预览 -->
                <div v-if="imgTo3dResult.modelUrl" class="model-viewer-wrapper clickable" @click="open3dModal(`/api/image/imgTo3d/proxy-model?modelUrl=${encodeURIComponent(imgTo3dResult.modelUrl)}`)">
                  <div class="preview-content">
                    <img v-if="imgTo3dResult.thumbnailUrl" :src="imgTo3dResult.thumbnailUrl" class="preview-thumb">
                    <div v-else class="no-preview-placeholder">
                      <i class="fas fa-cube"></i>
                    </div>
                    <div class="overlay">
                      <i class="fas fa-expand"></i>
                      <span>点击全屏查看模型</span>
                    </div>
                  </div>
                </div>
                <div v-else class="no-preview">
                  <p>暂无 3D 预览 (仅支持 GLB 格式预览)</p>
                  <img v-if="imgTo3dResult.thumbnailUrl" :src="imgTo3dResult.thumbnailUrl" class="static-preview">
                </div>

                <div class="result-info">
                   <div class="info-row">
                     <p class="status-text">状态: <span class="status-done">完成</span></p>
                     <p class="task-id">ID: {{ imgTo3dResult.taskId }}</p>
                   </div>

                   <div class="download-section" v-if="imgTo3dResult.resultFiles && imgTo3dResult.resultFiles.length > 0">
                     <div class="format-selector">
                       <label>下载格式：</label>
                       <select v-model="selectedFormat" class="format-select">
                         <option v-for="fmt in availableFormats" :key="fmt" :value="fmt">{{ fmt }}</option>
                       </select>
                     </div>
                     <a :href="currentDownloadUrl" target="_blank" class="download-btn-link" v-if="currentDownloadUrl">
                       <i class="fas fa-download"></i> 下载 {{ selectedFormat }} 文件
                     </a>
                   </div>
                </div>
             </div>
          </div>

          <!-- 图生3D 历史记录 -->
          <div class="history-section" v-if="imgTo3dHistory.length > 0">
            <h2>3D 生成历史</h2>
            <div class="history-grid">
              <div
                v-for="item in imgTo3dHistory"
                :key="item.taskId"
                class="gallery-item"
                @click="selectImgTo3dHistory(item)"
              >
                <img :src="item.thumbnailUrl" :alt="item.taskId" loading="lazy">
                <div class="item-overlay">
                  <p class="item-prompt">Task ID: {{ item.taskId }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- <div style="height: 400px; margin: 20px 0;">
        <three-viewer modelUrl="/api/image/imgTo3d/proxy-model?modelUrl=https%3A%2F%2Fhunyuan-prod-1258344699.cos.ap-guangzhou.tencentcos.cn%2F3d%2Foutput%2F1256347545%2F5aa55aab-d1ac-4b74-8e4e-bd97cce3639b_0.glb%3Fq-sign-algorithm%3Dsha1%26q-ak%3DAKIDpRovliU1IJ5ctufBSVIq8AwTlnZ5MN8d%26q-sign-time%3D1773224430%253B1773310830%26q-key-time%3D1773224430%253B1773310830%26q-header-list%3Dhost%26q-url-param-list%3D%26q-signature%3D5770cb1c10d79da488b4c5b32dd6b3e5a82dea91" />
      </div> -->

      <!-- 生成历史区域 -->
      <div class="history-section" v-if="creationMode === 'text-to-image' && generatedHistory.length > 0">
        <h2>生成历史</h2>
        <div class="history-grid">
          <div
            v-for="item in generatedHistory"
            :key="item.id"
            class="gallery-item"
            @click="openDetail(item)"
          >
            <img :src="item.imagePath" :alt="item.prompt" loading="lazy">
            <div class="item-overlay">
              <p class="item-prompt">{{ item.prompt }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- 推荐展示区域 -->
      <div class="gallery-section" >
        <div class="gallery-header">
          <h2>创作精选</h2>
          <div class="filter-controls">
            <div class="type-filter">
              <span
                :class="{ active: filterType === 'text2img' }"
                @click="filterType = 'text2img'"
              >文生图</span>
              <span
                :class="{ active: filterType === 'imgTo3d' }"
                @click="filterType = 'imgTo3d'"
              >图生3D</span>
            </div>
            <div class="search-box">
              <input
                type="text"
                v-model="searchKeyword"
                placeholder="搜索提示词..."
                class="search-input"
                @input="handleSearchInput"
              >
              <i class="fas fa-search search-icon"></i>
            </div>
          </div>
        </div>
        <div class="gallery-grid">
          <div
            v-for="(item, index) in recommendedWorks"
            :key="index"
            class="gallery-item"
            @click="openDetail(item)"
          >
            <img :src="item.imagePath" :alt="item.prompt" loading="lazy">
            <div class="item-overlay">
              <p class="item-prompt">{{ item.prompt }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 详情弹窗 -->
    <div v-if="showModal" class="modal-overlay" @click.self="closeDetail">
      <div class="modal-content">
        <button class="modal-close" @click="closeDetail">
          <i class="fas fa-times"></i>
        </button>
        <div class="modal-body">
          <div class="modal-image" @click="showImg">
            <img :src="selectedWork.imagePath" :alt="selectedWork.prompt" title="点击预览大图">
          </div>
          <div class="modal-info">
            <h3>提示词</h3>
            <p class="prompt-text">{{ selectedWork.prompt }}</p>
            <div class="modal-actions">
              <button class="action-btn download" @click="downloadImage(selectedWork)">
                <i class="fas fa-download"></i> 下载图片
              </button>
              <button class="action-btn copy" @click="copyPrompt(selectedWork.prompt)">
                <i class="fas fa-copy"></i> 复制提示词
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 图片预览组件 -->
    <vue-easy-lightbox
      :visible="visible"
      :imgs="selectedWork ? [selectedWork.imagePath] : []"
      :index="0"
      @hide="handleHide"
    ></vue-easy-lightbox>

    <!-- 充值对话框 -->
    <recharge-dialog
      :visible="showRechargeDialog"
      :returnUrl="currentUrl"
      :cost="creationCost"
      @close="showRechargeDialog = false"
    />

    <!-- 3D 模型查看弹窗 -->
    <div v-if="show3dModal" class="modal-overlay" @click.self="close3dModal">
      <div class="modal-content modal-3d">
        <button class="modal-close" @click="close3dModal">
          <i class="fas fa-times"></i>
        </button>
        <div class="modal-body-3d">
          <three-viewer :modelUrl="selected3dModelUrl" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueEasyLightbox from 'vue-easy-lightbox'
import { request } from '@/utils/request'
import RechargeDialog from '@/components/RechargeDialog.vue'
import ThreeViewer from '@/components/ThreeViewer.vue'

export default {
  name: 'AICreationView',
  components: {
    VueEasyLightbox,
    RechargeDialog,
    ThreeViewer
  },
  data() {
    return {
      prompt: '',
      selectedRatio: '1:1',
      isGenerating: false,
      generatedImage: null, // 存储生成后的图片信息
      generatedHistory: [], // 生成历史记录
      visible: false, // 控制 lightbox 显示
      ratios: [
        { label: '1:1', value: '1:1' },
        { label: '9:16', value: '9:16' },
        { label: '16:9', value: '16:9' },
        { label: '4:3', value: '4:3' },
        { label: '1:4', value: '1:4' },
        { label: '4:1', value: '4:1' },
        { label: '1:8', value: '1:8' },
        { label: '8:1', value: '8:1' },
        { label: '2:3', value: '2:3' },
        { label: '3:2', value: '3:2' },
        { label: '3:4', value: '3:4' },
        { label: '4:5', value: '4:5' },
        { label: '5:4', value: '5:4' },
        { label: '21:9', value: '21:9' }
      ],
      searchKeyword: '', // 搜索关键词
      filterType: 'text2img', // 筛选类型：text2img, imgTo3d
      searchTimeout: null, // 搜索防抖定时器
      recommendedWorks: [], // 推荐图片列表
      page: 1,
      pageSize: 20,
      total: 0,
      loading: false,
      showModal: false,
      selectedWork: null,
      hasMore: true,

      // 虚拟币相关
      coinBalance: 0,
      creationCost: 0,
      imgTo3dCost: 0,
      showRechargeDialog: false,
      currentUrl: '',
      isLoggedIn: false,

      // 图生3D相关
      creationMode: 'text-to-image',
      uploadedImage: null,
      uploadedImagePreview: '',
      
      // 文生图参考图片相关
      referenceImage: null,
      referenceImagePreview: '',
      showReferenceImageMenu: false,
      isImgTo3dGenerating: false,
      imgTo3dResult: null,
      imgTo3dHistory: [],
      selectedFormat: 'GLB',
      show3dModal: false,
      selected3dModelUrl: ''
    }
  },
  watch: {
    filterType() {
      this.page = 1;
      this.fetchRecommendedWorks();
    }
  },
  computed: {
    quickRatios() {
      return this.ratios.slice(0, 4);
    },
    currentDownloadUrl() {
      if (!this.imgTo3dResult || !this.imgTo3dResult.resultFiles) return '';
      const file = this.imgTo3dResult.resultFiles.find(f => f.Type.toUpperCase() === this.selectedFormat);
      return file ? file.Url : '';
    },
    availableFormats() {
      if (!this.imgTo3dResult || !this.imgTo3dResult.resultFiles) return [];
      return this.imgTo3dResult.resultFiles.map(f => f.Type.toUpperCase());
    }
  },
  mounted() {
    this.currentUrl = window.location.href;
    this.checkLoginStatus();
    this.loadCreationConfig();
    this.fetchRecommendedWorks();
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    async checkLoginStatus() {
      const token = localStorage.getItem('token');
      this.isLoggedIn = !!token;
      if (this.isLoggedIn) {
        try {
          const res = await request('/api/auth/self');
          if (res && res.code === 0) {
            this.coinBalance = res.data.coinBalance || 0;
          }
        } catch (error) {
          console.error('获取用户信息失败:', error);
        }
      }
    },
    async loadCreationConfig() {
      try {
        const [textToImgRes, imgTo3dRes] = await Promise.all([
          request('/api/coin/feature/text_to_img/config'),
          request('/api/coin/feature/img_to_3d/config')
        ]);

        if (textToImgRes && textToImgRes.code === 0) {
          this.creationCost = textToImgRes.data.cost || 0;
        }

        if (imgTo3dRes && imgTo3dRes.code === 0) {
          this.imgTo3dCost = imgTo3dRes.data.cost || 0;
        }
      } catch (error) {
        console.error('获取AI创作配置失败:', error);
      }
    },
    handleScroll() {
      if (this.loading || !this.hasMore) return;

      const scrollHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const clientHeight = document.documentElement.clientHeight;

      if (scrollHeight - scrollTop - clientHeight < 200) {
        this.page++;
        this.fetchRecommendedWorks();
      }
    },
    async fetchRecommendedWorks() {
      this.loading = true;
      try {
        const params = new URLSearchParams({
          pageNum: this.page,
          pageSize: this.pageSize,
          type: this.filterType
        });
        if (this.searchKeyword.trim()) {
          params.append('keyword', this.searchKeyword.trim());
        }

        const res = await request(`/api/aiImage/recommend/list?${params.toString()}`);
        if (res && res.code === 0) {
          const list = res.data.list || [];
          if (this.page === 1) {
            this.recommendedWorks = list;
          } else {
            this.recommendedWorks = [...this.recommendedWorks, ...list];
          }

          for (const item of list) {
            item.imagePath = '/upload/' + item.imagePath;
          }

          this.total = res.data.total || 0;
          this.hasMore = this.recommendedWorks.length < this.total;
        } else {
          this.$message.error(res ? res.msg : '获取推荐列表失败');
        }
      } catch (error) {
        console.error('获取推荐列表失败:', error);
        this.$message.error('获取推荐列表失败');
      } finally {
        this.loading = false;
      }
    },
    handleSearchInput() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.page = 1;
        this.fetchRecommendedWorks();
      }, 500);
    },
    handleSearch() {
      this.page = 1;
      this.fetchRecommendedWorks();
    },
    getRatioStyle(ratio) {
      const [w, h] = ratio.split(':').map(Number);
      return {
        width: '24px',
        height: `${24 * (h / w)}px`,
        border: '2px solid currentColor',
        borderRadius: '2px'
      };
    },
    async handleGenerate() {
      if (!this.prompt.trim() && !this.referenceImagePreview) return;

      if (this.creationCost > 0 && this.coinBalance < this.creationCost) {
        this.showRechargeDialog = true;
        this.$message.error(`虚拟豆余额不足，生成图片需要${this.creationCost}虚拟豆，当前余额${this.coinBalance}虚拟豆`);
        return;
      }

      this.isGenerating = true;
      try {
        const formData = new FormData();
        if (this.prompt.trim()) {
          formData.append('prompt', this.prompt.trim());
        }
        formData.append('aspectRatio', this.selectedRatio);
        
        // 如果有参考图片，以 base64 形式添加到表单数据中
        if (this.referenceImagePreview) {
          formData.append('referenceImage', this.referenceImagePreview);
        }

        // 调用文生图接口
        // 注意：request工具方法默认处理JSON，这里需要手动处理FormData或者修改request方法支持FormData
        // 由于request方法封装较为简单，这里直接使用fetch以支持FormData
        const token = localStorage.getItem('token');
        const response = await fetch('/api/image/textToImage', {
          method: 'POST',
          headers: {
            'x-access-token': token
          },
          body: formData
        });

        const data = await response.json();

        if (data.ok) {
          this.$message.success('生成成功');
          // 扣除虚拟豆
          if (this.creationCost > 0) {
            this.coinBalance -= this.creationCost;
          }
          // 将生成的图片展示在详情弹窗中
          const newWork = {
            id: Date.now(),
            imagePath: data.data, // 假设返回的是图片路径
            prompt: this.prompt
          };

          this.generatedHistory.unshift(newWork); // 添加到历史记录开头
          this.selectedWork = newWork;
          this.showModal = true;
        } else {
          if (data.code === 40001) {
            this.showRechargeDialog = true;
            this.$message.error(data.msg || '虚拟豆余额不足');
          } else {
            this.$message.error(data.msg || '生成失败，请重试');
          }
        }
      } catch (error) {
        console.error('生成图片失败:', error);
        this.$message.error('生成失败，请检查网络或重试');
      } finally {
        this.isGenerating = false;
      }
    },
    openDetail(work) {
      this.selectedWork = work;
      this.showModal = true;
      document.body.style.overflow = 'hidden'; // 防止背景滚动
    },
    closeDetail() {
      this.showModal = false;
      this.selectedWork = null;
      document.body.style.overflow = '';
    },
    showImg() {
      this.visible = true;
    },
    handleHide() {
      this.visible = false;
    },
    downloadImage(work) {
      // 简单的下载逻辑
      const a = document.createElement('a');
      a.href = work.imagePath;
      a.download = `ai-creation-${work.id}.jpg`;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },
    handleImageUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      if (file.size > 10 * 1024 * 1024) {
        this.$message.error('图片大小不能超过10MB');
        return;
      }

      this.uploadedImage = file;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.uploadedImagePreview = e.target.result;
      };
      reader.readAsDataURL(file);
    },
    handleReferenceImageUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      if (file.size > 10 * 1024 * 1024) {
        this.$message.error('图片大小不能超过10MB');
        return;
      }

      this.referenceImage = file;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.referenceImagePreview = e.target.result;
        this.showReferenceImageMenu = false;
      };
      reader.readAsDataURL(file);
    },
    removeReferenceImage() {
      this.referenceImage = null;
      this.referenceImagePreview = '';
      this.showReferenceImageMenu = false;
      if (this.$refs.referenceImageInput) {
        this.$refs.referenceImageInput.value = '';
      }
    },
    toggleReferenceImageMenu() {
      this.showReferenceImageMenu = !this.showReferenceImageMenu;
      // 点击其他地方关闭菜单
      if (this.showReferenceImageMenu) {
        setTimeout(() => {
          document.addEventListener('click', this.closeReferenceImageMenu);
        }, 0);
      }
    },
    closeReferenceImageMenu(event) {
      if (!this.$el.querySelector('.reference-image-option')?.contains(event.target)) {
        this.showReferenceImageMenu = false;
        document.removeEventListener('click', this.closeReferenceImageMenu);
      }
    },
    triggerImageUpload() {
      this.showReferenceImageMenu = false;
      this.$refs.referenceImageInput.click();
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    async handleImgTo3dGenerate() {
      if (!this.uploadedImagePreview) {
        this.$message.warning('请先上传图片');
        return;
      }

      if (this.imgTo3dCost > 0 && this.coinBalance < this.imgTo3dCost) {
        this.showRechargeDialog = true;
        this.$message.error(`虚拟豆余额不足，生成3D模型需要${this.imgTo3dCost}虚拟豆，当前余额${this.coinBalance}虚拟豆`);
        return;
      }

      this.isImgTo3dGenerating = true;
      this.imgTo3dResult = null;

      try {
        const res = await request('/api/image/imgTo3d/submit', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            imageDataUrl: this.uploadedImagePreview
          })
        });

        if (res && res.code === 0) {
          const taskId = res.data;
          this.$message.success('任务提交成功，正在生成中...');

          // 扣除虚拟豆
          if (this.imgTo3dCost > 0) {
            this.coinBalance -= this.imgTo3dCost;
          }

          this.pollImgTo3dTask(taskId);
        } else {
          this.$message.error(res ? res.msg : '提交任务失败');
          this.isImgTo3dGenerating = false;
        }
      } catch (error) {
        console.error('提交任务失败:', error);
        this.$message.error('提交任务失败');
        this.isImgTo3dGenerating = false;
      }
    },
    async pollImgTo3dTask(taskId) {
      const poll = async () => {
        try {
          const res = await request('/api/image/imgTo3d/query', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ id: taskId })
          });

          if (res && res.code === 0) {
            // 解析返回结构
            const data = res.data;
            let status = '';
            let resultFiles = [];

            // 兼容不同的返回结构，优先处理用户提供的 Response 结构
            if (data && data.Response) {
              status = data.Response.Status;
              resultFiles = data.Response.ResultFile3Ds || [];
            } else {
              // 备用：假设直接返回了字段
              status = data.status || data.Status;
              resultFiles = data.ResultFile3Ds || [];
            }

            if (status === 'DONE' || status === 'succeeded' || status === 'completed') {
               const glbFile = resultFiles.find(f => f.Type.toUpperCase() === 'GLB');
               const previewFile = resultFiles.length > 0 ? resultFiles[0] : null;

               const result = {
                 status: '完成',
                 modelUrl: glbFile ? glbFile.Url : '', // 用于在线预览，优先 GLB
                 thumbnailUrl: previewFile ? previewFile.PreviewImageUrl : '',
                 taskId: taskId,
                 timestamp: Date.now(),
                 resultFiles: resultFiles // 保存完整文件列表
               };

               this.imgTo3dResult = result;
               this.selectedFormat = 'GLB'; // 默认选择 GLB
               this.imgTo3dHistory.unshift(result);
               this.$message.success('生成成功');
               this.isImgTo3dGenerating = false;
            } else if (status === 'FAILED' || status === 'failed') {
               this.$message.error('生成失败');
               this.isImgTo3dGenerating = false;
            } else {
               // processing or pending
               setTimeout(poll, 2000);
            }
          } else {
            this.$message.error('查询任务状态失败');
            this.isImgTo3dGenerating = false;
          }
        } catch (error) {
          console.error('查询任务状态失败:', error);
          this.isImgTo3dGenerating = false;
        }
      };

      poll();
    },
    copyPrompt(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$message.success('提示词已复制');
      }).catch(() => {
        this.$message.error('复制失败');
      });
    },
    selectImgTo3dHistory(item) {
      this.imgTo3dResult = item;
      this.selectedFormat = 'GLB';

      // 滚动到顶部或结果区域
      this.$nextTick(() => {
        const resultSection = this.$el.querySelector('.result-section');
        if (resultSection) {
          resultSection.scrollIntoView({ behavior: 'smooth' });
        }
      });
    },
    open3dModal(modelUrl) {
      if (!modelUrl) return;
      this.selected3dModelUrl = modelUrl;
      this.show3dModal = true;
      document.body.style.overflow = 'hidden';
    },
    close3dModal() {
      this.show3dModal = false;
      this.selected3dModelUrl = '';
      document.body.style.overflow = '';
    }
  }
}
</script>

<style scoped>
.ai-creation-page {
  padding-top: 80px; /* 避开 Navbar */
  min-height: 100vh;
  background-color: #f8f9fa;
  padding-bottom: 40px;
}

.creation-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.creation-header {
  text-align: center;
  margin-bottom: 40px;
}

.creation-header h1 {
  font-size: 2.5rem;
  color: #333;
  margin-bottom: 10px;
}

.creation-header p {
  color: #666;
  font-size: 1.1rem;
}

.creation-panel {
  background: white;
  border-radius: 16px;
  padding: 30px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.05);
  margin-bottom: 60px;
}

.input-section {
  margin-bottom: 24px;
}

.input-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.input-label {
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  gap: 8px;
}

.coin-tip {
  font-size: 14px;
  color: #666;
  background: #fff9c4;
  padding: 4px 12px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.coin-amount {
  font-weight: bold;
  color: #f57c00;
}

.textarea-wrapper {
  position: relative;
  width: 100%;
}

.prompt-input {
  width: 100%;
  padding: 16px;
  padding-bottom: 30px; /* 留出字数统计的空间 */
  border: 2px solid #e0e0e0;
  border-radius: 16px;
  font-size: 16px;
  line-height: 1.6;
  resize: vertical;
  transition: all 0.3s ease;
  outline: none;
  background: #fafafa;
  box-shadow: inset 0 2px 4px rgba(0,0,0,0.02);
}

.prompt-input:focus {
  border-color: #2196F3;
  background: white;
  box-shadow: 0 0 0 4px rgba(33, 150, 243, 0.1);
}

.prompt-input::placeholder {
  color: #aaa;
}

.word-count {
  position: absolute;
  bottom: 12px;
  right: 16px;
  font-size: 12px;
  color: #999;
  pointer-events: none;
}

.settings-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
}

.ratio-selector {
  display: flex;
  align-items: center;
  gap: 12px;
}

.label {
  font-weight: 500;
  color: #333;
}

.ratio-options {
  display: flex;
  gap: 10px;
}

.ratio-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
  color: #666;
}

.ratio-btn.active {
  background: #e3f2fd;
  border-color: #2196F3;
  color: #2196F3;
}

.ratio-btn:hover:not(.active) {
  background: #f5f5f5;
}

.ratio-icon {
  background: transparent;
}

.ratio-dropdown-wrapper {
  position: relative;
}

.ratio-dropdown {
  padding: 8px 32px 8px 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  font-size: 14px;
  color: #666;
  outline: none;
  transition: all 0.2s;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
}

.ratio-dropdown:focus {
  border-color: #2196F3;
  box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
}

.ratio-dropdown:hover {
  border-color: #bbb;
}

.generate-btn {
  padding: 12px 32px;
  background: linear-gradient(135deg, #2196F3, #1976D2);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.generate-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3);
}

.generate-btn:disabled {
  background: #bdbdbd;
  cursor: not-allowed;
}

.settings-left {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.reference-image-option {
  position: relative;
}

.option-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 24px;
  background: white;
  cursor: pointer;
  transition: all 0.3s;
  color: #666;
  font-weight: 500;
}

.option-btn:hover {
  border-color: #2196F3;
  color: #2196F3;
}

.option-btn.has-image {
  border-color: #4CAF50;
  background: #e8f5e9;
  color: #2E7D32;
}

.reference-image-thumbnail {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid rgba(0,0,0,0.1);
}

.reference-image-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.menu-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.change-image-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  background: #f5f5f5;
  color: #666;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 4px;
}

.change-image-btn:hover {
  background: #2196F3;
  color: white;
}

.reference-image-menu {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 8px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  min-width: 280px;
  z-index: 100;
  overflow: hidden;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.2s;
  color: #333;
}

.menu-item:hover {
  background: #f5f5f5;
}

.menu-item i {
  color: #666;
  font-size: 16px;
}

.image-preview-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #fafafa;
}

.menu-preview-img {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 8px;
}

.image-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.image-name {
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.image-size {
  color: #999;
  font-size: 12px;
}

.remove-menu-btn {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #f5f5f5;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  transition: all 0.2s;
}

.remove-menu-btn:hover {
  background: #ff5252;
  color: white;
}

.gallery-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.gallery-header h2 {
  margin: 0;
  color: #333;
  font-size: 1.8rem;
}

.filter-controls {
  display: flex;
  align-items: center;
  gap: 20px;
}

.type-filter {
  display: flex;
  background: #f0f0f0;
  border-radius: 20px;
  padding: 4px;
}

.type-filter span {
  padding: 6px 16px;
  border-radius: 16px;
  cursor: pointer;
  font-size: 14px;
  color: #666;
  transition: all 0.3s;
}

.type-filter span.active {
  background: white;
  color: #2196F3;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  font-weight: 500;
}

.search-box {
  position: relative;
  width: 260px;
}

.search-input {
  width: 100%;
  padding: 10px 16px 10px 36px;
  border: 1px solid #ddd;
  border-radius: 20px;
  font-size: 14px;
  outline: none;
  transition: all 0.3s;
}

.search-input:focus {
  border-color: #2196F3;
  box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
}

.gallery-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;
}

.gallery-item {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  transition: transform 0.3s;
  aspect-ratio: 1; /* 默认正方形展示，也可以根据图片实际比例 */
}

.gallery-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.gallery-item:hover {
  transform: translateY(-4px);
}

.item-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0,0,0,0.8), transparent);
  padding: 20px 16px;
  opacity: 0;
  transition: opacity 0.3s;
}

.gallery-item:hover .item-overlay {
  opacity: 1;
}

.item-prompt {
  color: white;
  font-size: 14px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin: 0;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 16px;
  width: 1000px;
  max-width: 95vw;
  height: 700px;
  max-height: 90vh;
  display: flex;
  overflow: hidden;
  position: relative;
  animation: modalFadeIn 0.3s ease;
}

@keyframes modalFadeIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}

.modal-close {
  position: absolute;
  top: 16px;
  right: 16px;
  background: rgba(0,0,0,0.5);
  color: white;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-close:hover {
  background: rgba(0,0,0,0.7);
}

.modal-body {
  display: flex;
  width: 100%;
}

.modal-image {
  flex: 1.5;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: zoom-in;
}

.modal-image img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transition: transform 0.3s;
}

.modal-info {
  flex: 1;
  padding: 30px;
  display: flex;
  flex-direction: column;
  background: white;
  overflow-y: auto;
}

.modal-info h3 {
  margin-top: 0;
  margin-bottom: 16px;
  color: #333;
}

.prompt-text {
  color: #555;
  line-height: 1.6;
  background: #f5f5f5;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;
  white-space: pre-wrap;
}

.modal-actions {
  margin-top: auto;
  display: flex;
  gap: 12px;
}

.action-btn {
  flex: 1;
  padding: 10px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.2s;
}

.action-btn.download {
  background: #4CAF50;
  color: white;
}

.action-btn.download:hover {
  background: #43A047;
}

.action-btn.copy {
  background: #f0f0f0;
  color: #333;
}

.action-btn.copy:hover {
  background: #e0e0e0;
}

/* Responsive adjustments */
@media (max-width: 900px) {
  .modal-content {
    flex-direction: column;
    height: auto;
    max-height: 90vh;
    overflow-y: auto;
  }

  .modal-body {
    flex-direction: column;
    height: auto;
  }

  .modal-image {
    height: 40vh;
    flex: none;
    width: 100%;
  }

  .modal-info {
    flex: none;
    padding: 20px;
    height: auto;
  }
}

@media (max-width: 768px) {
  .settings-section {
    flex-direction: column;
    align-items: stretch;
  }

  .generate-btn {
    width: 100%;
    justify-content: center;
  }
}

.history-section {
  margin-bottom: 50px;
}

.history-section h2 {
  margin-bottom: 24px;
  color: #333;
  font-size: 1.8rem;
}

.history-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;
}

/* Tabs */
.creation-tabs {
  display: flex;
  margin-bottom: 24px;
  border-bottom: 2px solid #eee;
}

.tab-item {
  padding: 12px 24px;
  font-weight: 600;
  color: #666;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
}

.tab-item.active {
  color: #2196F3;
  border-bottom-color: #2196F3;
}

.tab-item:hover:not(.active) {
  color: #333;
  background: #f5f5f5;
  border-radius: 8px 8px 0 0;
}

/* Upload Area */
.upload-area {
  width: 100%;
  min-height: 300px;
  border: 2px dashed #e0e0e0;
  border-radius: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s;
  background: #fafafa;
  overflow: hidden;
  position: relative;
}

.upload-area:hover {
  border-color: #2196F3;
  background: #f0f7ff;
}

.upload-area.disabled {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
  background: #f5f5f5;
  border-color: #e0e0e0;
}

.upload-placeholder {
  text-align: center;
  color: #999;
}

.upload-placeholder i {
  font-size: 48px;
  margin-bottom: 16px;
  color: #bdbdbd;
}

.upload-placeholder p {
  font-size: 16px;
  margin: 0;
}

.upload-placeholder .sub-text {
  font-size: 12px;
  margin-top: 8px;
}

.preview-container {
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}

.preview-img {
  max-width: 100%;
  max-height: 300px;
  object-fit: contain;
}

.reupload-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  opacity: 0;
  transition: opacity 0.3s;
  font-weight: 600;
  gap: 8px;
}

.preview-container:hover .reupload-overlay {
  opacity: 1;
}

.remove-image-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 82, 82, 0.9);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
  z-index: 10;
}

.remove-image-btn:hover {
  background: #ff5252;
  transform: scale(1.1);
}

/* Result Section */
.result-section {
  margin-top: 40px;
  border-top: 1px solid #eee;
  padding-top: 24px;
}

.result-card {
  background: #f5f5f5;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.model-viewer-wrapper {
  width: 100%;
  height: 400px;
  background: #ddd;
  border-radius: 8px;
  overflow: hidden;
}

.no-preview {
  width: 100%;
  height: 300px;
  background: #eee;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #666;
  gap: 16px;
}

.static-preview {
  max-width: 200px;
  max-height: 200px;
  border-radius: 4px;
}

.result-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.status-text {
  font-weight: 600;
  color: #333;
}

.status-done {
  color: #4CAF50;
  margin-left: 8px;
}

.task-id {
  font-size: 12px;
  color: #999;
  font-family: monospace;
}

.download-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  padding: 12px;
  border-radius: 8px;
}

.format-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
}

.format-select {
  padding: 6px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  outline: none;
  font-size: 14px;
}

.download-btn-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #4CAF50;
  color: white;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 500;
  transition: background 0.3s;
}

.download-btn-link:hover {
  background: #43A047;
}

.result-thumbnail {
  margin-top: 16px;
  max-width: 200px;
  border-radius: 8px;
  overflow: hidden;
}

.result-thumbnail img {
  width: 100%;
  display: block;
}

.model-viewer-wrapper.clickable {
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #2c3e50;
  transition: transform 0.2s;
}

.model-viewer-wrapper.clickable:hover {
  transform: scale(1.01);
}

.preview-content {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.preview-thumb {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.no-preview-placeholder {
  color: #fff;
  font-size: 64px;
  opacity: 0.5;
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.4);
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  opacity: 0;
  transition: opacity 0.3s;
  font-size: 18px;
  font-weight: 500;
}

.model-viewer-wrapper:hover .overlay {
  opacity: 1;
}

.modal-3d {
  width: 90vw;
  height: 90vh;
  background: #2c3e50;
  max-width: none;
  max-height: none;
  display: flex;
  flex-direction: column;
  padding: 0;
}

.modal-body-3d {
  flex: 1;
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
}

.modal-3d .modal-close {
  top: 20px;
  right: 20px;
  background: rgba(255,255,255,0.2);
}

.modal-3d .modal-close:hover {
  background: rgba(255,255,255,0.4);
}
</style>
